import { incrementTelemetry } from 'core-events';
import { useState } from 'react';
import { JSX } from 'react';
import { getSearchUrl, SearchFilterContentTypes, SearchOrigins } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';
import { SearchSimilar } from '@videoblocks/react-icons';

import IconButton from '../../atoms/IconButton';

export type SearchSimilarButtonProps = {
    isDisabled?: boolean;
    selectedContentType: SearchFilterContentTypes;
    contentId: number;
    className?: string;
};

const SearchSimilarButton = ({
    isDisabled = false,
    selectedContentType,
    contentId,
    className = '',
}: SearchSimilarButtonProps): JSX.Element => {
    const [showTooltip, setShowTooltip] = useState(false);
    const onClick = () => {
        if (!isDisabled) {
            void incrementTelemetry('audio.search.similar.click');
        }
    };

    const onHover = () => {
        if (!isDisabled) {
            void incrementTelemetry('audio.search.similar.hover');
        }
        setShowTooltip(true);
    };

    const onMouseLeave = () => {
        setShowTooltip(false);
    };

    const iconButtonAccesibleText = isDisabled ? 'No Similar Tracks Available' : 'Search Similar';

    const iconButton = (
        <IconButton
            className={twMerge('hidden lg:inline-flex', className)}
            onMouseEnter={onHover}
            onMouseLeave={onMouseLeave}
            onFocus={onHover}
            onKeyDown={onClick}
            onClick={onClick}
            as="link"
            href={getSearchUrl({
                contentType: selectedContentType,
                searchOrigin: SearchOrigins.STOCK_ITEM_CARD,
                searchSimilarId: contentId,
            })}
            aria-label={iconButtonAccesibleText}
        >
            <SearchSimilar className="fill-current" />
        </IconButton>
    );

    const iconButtonDisabled = (
        <div
            className={twMerge('hidden lg:inline-flex', className)}
            onMouseEnter={onHover}
            onMouseLeave={onMouseLeave}
            onFocus={onHover}
        >
            <IconButton disabled={isDisabled} aria-label={iconButtonAccesibleText} title={iconButtonAccesibleText}>
                <SearchSimilar className="cursor-not-allowed fill-current text-gray-500" />
            </IconButton>
        </div>
    );

    const trigger = isDisabled ? iconButtonDisabled : iconButton;

    const getTooltipTitle = () => (isDisabled ? 'No Similar Tracks Available' : 'Search Similar');

    return <Tooltip open={showTooltip} title={getTooltipTitle()} trigger={trigger} />;
};

export default SearchSimilarButton;
